import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { X } from "lucide-react";
import {
  createDashboard,
  deleteDashboard,
  listDashboards,
  listWorkspacesAll,
} from "./api/boardApi";
import { errorToast, successToast } from "../../redux/actionCreator";
import "./dashboards.scss";

// List dashboards across workspaces. Click → /dashboards/:id (viewer).
// Admins can create new dashboards from this page.
export default function DashboardsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "", workspace_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [d, w] = await Promise.all([listDashboards(), listWorkspacesAll()]);
      setItems(d.data || []);
      setWorkspaces(w.data || []);
      if (w.data?.length && !draft.workspace_id) {
        setDraft((x) => ({ ...x, workspace_id: w.data[0]._id }));
      }
    } catch (e) { errorToast(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return;
    try {
      const res = await createDashboard(draft);
      setShowCreate(false);
      setDraft({ name: "", description: "", workspace_id: workspaces[0]?._id || "" });
      successToast("Dashboard created");
      navigate(`/dashboards/${res.data._id}`);
    } catch (e) { errorToast(e.message); }
  };

  const remove = async (e, d) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await deleteDashboard(d._id);
      setItems((arr) => arr.filter((x) => x._id !== d._id));
    } catch (err) { errorToast(err.message); }
  };

  return (
    <div className="dashboards-list-page">
      <header className="dashboards-head">
        <h2>Dashboards</h2>
        <Button variant="primary" onClick={() => setShowCreate(true)}>+ New dashboard</Button>
      </header>

      {loading && <div className="muted"><Spinner size="sm" animation="border" /> Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="empty-state">
          <p>No dashboards yet.</p>
          <Button variant="primary" onClick={() => setShowCreate(true)}>Create your first dashboard</Button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="dashboard-grid">
          {items.map((d) => {
            const ws = workspaces.find((w) => String(w._id) === String(d.workspace_id));
            return (
              <div
                key={d._id}
                className="dashboard-card"
                onClick={() => navigate(`/dashboards/${d._id}`)}
              >
                <header className="dashboard-card-head">
                  <h4>{d.name}</h4>
                  <button className="dashboard-card-del" onClick={(e) => remove(e, d)} title="Delete" aria-label="Delete dashboard"><X size={14} /></button>
                </header>
                {d.description && <p className="dashboard-card-desc">{d.description}</p>}
                <div className="dashboard-card-meta">
                  {ws && <span className="workspace-tag" style={{ background: ws.color }}>{ws.name}</span>}
                  <span>{(d.widgets || []).length} widgets</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title>New dashboard</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control autoFocus value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Workspace *</Form.Label>
            <Form.Select value={draft.workspace_id} onChange={(e) => setDraft((d) => ({ ...d, workspace_id: e.target.value }))}>
              {workspaces.map((w) => <option key={w._id} value={w._id}>{w.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" disabled={!draft.name.trim() || !draft.workspace_id} onClick={create}>Create</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
